import React, { useMemo, useContext } from 'react';

import CardSlider from 'components/CardSlider';
import { useCustomCardSliderQuery } from 'hooks/useCustomCardSliderQuery';
import useLiveData from 'hooks/useLiveData';
import { PersonalizationContext } from 'components/Personalization/PersonalizationContext';

const CustomCardSliderProvider = ({ children, staticData, id, ...props }) => {
	const { matchAudience } = useContext(PersonalizationContext);
	const { data } = useCustomCardSliderQuery();
	const shouldLimitLiveDataByStaticIds = !staticData;
	const effectiveStaticData = React.useMemo(() => staticData || data || {}, [staticData, data]);
	const customCardSliderEdges = effectiveStaticData?.allCustomCardSlider?.edges || [];
	const customCardSliderNodes = customCardSliderEdges.map(({ node }) => node);

	const {
		statusText: customCardSliderStatusText,
		response: customCardSliderResponse,
		visibilityChange: customCardSliderVisibilityChange,
	} = useLiveData({
		namespace: 'SmartTags',
		model: 'CustomCardSlider',
		method: 'all',
		graphqlKey: 'allSmartTagsCustomCardSlider',
		apiParams: {
			archived: false,
			approved: true,
			deleted: false,
			_join: 'Audience',
			...(shouldLimitLiveDataByStaticIds && customCardSliderNodes.length > 0 ? {
				id: customCardSliderNodes.map((n) => n.id).join(','),
			} : {}),
			...(!!id ? { id } : {}),
		},
		staticData: effectiveStaticData,
	});

	const liveCustomCardSlider = customCardSliderResponse?.data[0];
	let customCardSlider = customCardSliderNodes[0];
	if (!!liveCustomCardSlider && customCardSliderStatusText === 'LOADED') {
		customCardSlider = liveCustomCardSlider;
	}
	if (!!liveCustomCardSlider && customCardSliderVisibilityChange) customCardSlider = liveCustomCardSlider;

	const datum = useMemo(() => customCardSlider, [customCardSlider]);

	const slides = datum?.cards.map((card) => {
		return {
			...card,
			cta: card?.linkText ?? 'Learn More',
			type: datum?.cardType ?? 'blog',
		}
	})

	if (!!datum && datum.hasOwnProperty('Audience') && datum?.Audience) {
		if (!matchAudience(datum?.Audience)) {
			return null;
		}
	}

	return (
		<CardSlider 
			subtitle={datum?.description} 
			{...datum} 
			slider={{slides: slides}} 
			layout={props.layout ?? 'fullwidth'} 
			flush={true} 
			matchAudience={matchAudience}
		/>
	);
};

export default CustomCardSliderProvider;